import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CircleAlert, PackageOpen, Search, SearchX, X } from 'lucide-react';
import PageHeader from '../../components/site/PageHeader';
import EmptyState from '../../components/site/EmptyState';
import ProductCard, { ProductCardSkeleton } from '../../components/productCard';
import { categoriesAPI, productsAPI } from '../../api/apiService';
import { getErrorMessage } from '../../utils/apiError';
import { Pagination } from '../../ui';
import './style.css';

const PAGE_SIZE = 12;
const SEARCH_DELAY_MS = 300;

const SORTS = [
  { value: 'newest', label: 'Newest', sortBy: 'createddate', sortOrder: 'desc' },
  { value: 'price-asc', label: 'Price: low to high', sortBy: 'price', sortOrder: 'asc' },
  { value: 'price-desc', label: 'Price: high to low', sortBy: 'price', sortOrder: 'desc' },
  { value: 'name-asc', label: 'Name: A to Z', sortBy: 'name', sortOrder: 'asc' },
];
const DEFAULT_SORT = SORTS[0].value;

const readPage = value => {
  const page = parseInt(value, 10);
  return Number.isFinite(page) && page > 0 ? page : 1;
};

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

const AllProductsPage = () => {
  // All filter state lives in the URL: ?q=&category=&sort=&page=
  const [searchParams, setSearchParams] = useSearchParams();
  const q = (searchParams.get('q') || '').trim();
  const category = searchParams.get('category') || '';
  const sortParam = searchParams.get('sort');
  const sortKey = SORTS.some(s => s.value === sortParam) ? sortParam : DEFAULT_SORT;
  const page = readPage(searchParams.get('page'));

  const [searchInput, setSearchInput] = useState(q);
  const lastQ = useRef(q);
  const toolbarRef = useRef(null);

  const [categories, setCategories] = useState([]);
  const [result, setResult] = useState({
    status: 'loading',
    products: [],
    totalCount: 0,
    totalPages: 0,
    error: null,
  });
  const [reloadKey, setReloadKey] = useState(0);

  // Changes the given params (null/'' removes one) and keeps the rest.
  const updateParams = useCallback(
    (changes, options) => {
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        Object.entries(changes).forEach(([key, value]) => {
          if (value === null || value === undefined || value === '') next.delete(key);
          else next.set(key, String(value));
        });
        return next;
      }, options);
    },
    [setSearchParams]
  );

  // Back/forward or a link changed the search: show it in the box.
  useEffect(() => {
    if (q !== lastQ.current) {
      lastQ.current = q;
      setSearchInput(q);
    }
  }, [q]);

  // Typing updates the URL after a short pause (replacing, so history isn't one entry per key).
  useEffect(() => {
    const term = searchInput.trim();
    if (term === lastQ.current) return undefined;
    const timer = setTimeout(() => {
      lastQ.current = term;
      updateParams({ q: term, page: null }, { replace: true });
    }, SEARCH_DELAY_MS);
    return () => clearTimeout(timer);
  }, [searchInput, updateParams]);

  // Active categories, in the admin's display order.
  useEffect(() => {
    let active = true;
    categoriesAPI
      .getAll({ isActive: true, pageSize: 100 })
      .then(response => {
        if (!active) return;
        const list = (response.data?.data || [])
          .filter(c => c?.name && c.isActive !== false)
          .sort(
            (a, b) =>
              (a.displayOrder ?? 0) - (b.displayOrder ?? 0) ||
              a.name.localeCompare(b.name)
          );
        setCategories(list.map(c => c.name));
      })
      .catch(() => {
        // Chips are optional; the "All" chip and URL filters still work.
        if (active) setCategories([]);
      });
    return () => {
      active = false;
    };
  }, []);

  // One server query per filter state.
  useEffect(() => {
    const controller = new AbortController();
    const sort = SORTS.find(s => s.value === sortKey);

    setResult(prev => ({ ...prev, status: 'loading', error: null }));
    productsAPI
      .getAll(
        {
          searchTerm: q,
          category,
          currentPage: page,
          itemsPerPage: PAGE_SIZE,
          sortBy: sort.sortBy,
          sortOrder: sort.sortOrder,
        },
        { signal: controller.signal }
      )
      .then(response => {
        const data = response.data?.data || {};
        setResult({
          status: 'ready',
          products: data.products || [],
          totalCount: Number(data.totalCount) || 0,
          totalPages: Number(data.totalPages) || 0,
          error: null,
        });
      })
      .catch(error => {
        if (controller.signal.aborted) return;
        setResult({
          status: 'error',
          products: [],
          totalCount: 0,
          totalPages: 0,
          error: getErrorMessage(error, "We couldn't load the projects. Please try again."),
        });
      });

    return () => controller.abort();
  }, [q, category, sortKey, page, reloadKey]);

  // A deep link past the last page goes to the last page.
  useEffect(() => {
    if (result.status === 'ready' && result.totalPages > 0 && page > result.totalPages) {
      updateParams({ page: result.totalPages > 1 ? result.totalPages : null }, { replace: true });
    }
  }, [result.status, result.totalPages, page, updateParams]);

  const chips = useMemo(
    () => (category && !categories.includes(category) ? [...categories, category] : categories),
    [categories, category]
  );

  const hasFilters = Boolean(q || category);

  const scrollToResults = () => {
    toolbarRef.current?.scrollIntoView({
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
      block: 'start',
    });
  };

  const selectCategory = name => {
    updateParams({ category: name, page: null });
  };

  const selectSort = value => {
    updateParams({ sort: value === DEFAULT_SORT ? null : value, page: null });
  };

  const goToPage = next => {
    updateParams({ page: next > 1 ? next : null });
    scrollToResults();
  };

  const submitSearch = event => {
    event.preventDefault();
    const term = searchInput.trim();
    lastQ.current = term;
    updateParams({ q: term, page: null });
  };

  const clearSearch = () => {
    setSearchInput('');
    lastQ.current = '';
    updateParams({ q: null, page: null });
  };

  const clearFilters = () => {
    setSearchInput('');
    lastQ.current = '';
    updateParams({ q: null, category: null, page: null });
  };

  const { status, products, totalCount, error } = result;
  const isLoading = status === 'loading';

  let countText = '';
  if (status === 'ready') {
    countText = `${totalCount} ${totalCount === 1 ? 'project' : 'projects'}`;
    if (q) countText += ` matching “${q}”`;
    if (category) countText += ` in ${category}`;
  }

  return (
    <main className="pk-page catalog-page">
      <div className="pk-container">
        <PageHeader
          title="All projects"
          subtitle="Browse complete projects with full source code."
        />

        <div className="catalog-toolbar" ref={toolbarRef}>
          <form className="catalog-search" role="search" onSubmit={submitSearch}>
            <label className="sr-only" htmlFor="catalog-search">
              Search projects
            </label>
            <div className="pk-input-icon">
              <Search aria-hidden="true" />
              <input
                id="catalog-search"
                className="pk-input catalog-search__input"
                type="search"
                placeholder="Search by name, technology or keyword"
                autoComplete="off"
                enterKeyHint="search"
                value={searchInput}
                onChange={e => setSearchInput(e.target.value)}
              />
              {searchInput && (
                <button
                  type="button"
                  className="catalog-search__clear"
                  onClick={clearSearch}
                  aria-label="Clear search"
                >
                  <X aria-hidden="true" />
                </button>
              )}
            </div>
          </form>

          {/* On phones the category chips collapse into this select. */}
          <div className="catalog-select catalog-select--category">
            <label className="pk-label" htmlFor="catalog-category">
              Category
            </label>
            <select
              id="catalog-category"
              className="pk-select"
              value={category}
              onChange={e => selectCategory(e.target.value)}
            >
              <option value="">All categories</option>
              {chips.map(name => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
          </div>

          <div className="catalog-select">
            <label className="pk-label" htmlFor="catalog-sort">
              Sort by
            </label>
            <select
              id="catalog-sort"
              className="pk-select"
              value={sortKey}
              onChange={e => selectSort(e.target.value)}
            >
              {SORTS.map(s => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="catalog-chips" role="group" aria-label="Filter by category">
          <button
            type="button"
            className="pk-chip"
            aria-pressed={!category}
            onClick={() => selectCategory('')}
          >
            All
          </button>
          {chips.map(name => (
            <button
              key={name}
              type="button"
              className="pk-chip"
              aria-pressed={category === name}
              onClick={() => selectCategory(name)}
            >
              {name}
            </button>
          ))}
        </div>

        <p className="catalog-count" aria-live="polite">
          {isLoading ? (
            <>
              <span className="pk-skel catalog-count__skel" aria-hidden="true" />
              <span className="sr-only">Loading projects</span>
            </>
          ) : (
            countText
          )}
        </p>

        <section aria-label="Projects" aria-busy={isLoading || undefined}>
          {status === 'error' ? (
            <EmptyState
              role="alert"
              icon={<CircleAlert />}
              title="Couldn't load projects"
              text={error}
              actions={
                <button
                  type="button"
                  className="pk-btn pk-btn--primary pk-btn--sm"
                  onClick={() => setReloadKey(k => k + 1)}
                >
                  Retry
                </button>
              }
            />
          ) : isLoading ? (
            <ul className="catalog-grid">
              {Array.from({ length: PAGE_SIZE }, (_, i) => (
                <li key={i}>
                  <ProductCardSkeleton />
                </li>
              ))}
            </ul>
          ) : products.length === 0 ? (
            hasFilters ? (
              <EmptyState
                icon={<SearchX />}
                title="No projects match"
                text="Try a different search or category."
                actions={
                  <button
                    type="button"
                    className="pk-btn pk-btn--secondary pk-btn--sm"
                    onClick={clearFilters}
                  >
                    Clear filters
                  </button>
                }
              />
            ) : (
              <EmptyState
                icon={<PackageOpen />}
                title="No projects yet"
                text="Check back soon."
              />
            )
          ) : (
            <ul className="catalog-grid">
              {products.map(product => (
                <li key={product.id}>
                  <ProductCard product={product} />
                </li>
              ))}
            </ul>
          )}

          {status !== 'error' && totalCount > PAGE_SIZE && (
            <Pagination
              className="catalog-pagination"
              align="center"
              current={Math.min(page, Math.max(1, result.totalPages))}
              total={totalCount}
              pageSize={PAGE_SIZE}
              onChange={goToPage}
            />
          )}
        </section>
      </div>
    </main>
  );
};

export default AllProductsPage;
